import React from 'react';
import {SettingsLinkRow, SettingsRow} from 'app/components/settings/settingsLinkRow';
import {Button, COLORS, Typography} from '@fupa/fupa-uikit';
import {LinkOnComponent} from 'app/components/links/link';
import styled from 'styled-components';
import {Avatar} from 'app/components/avatar/Avatar';
import {FlexContainer} from 'app/styles/content';
import {useLocation} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';
import {selectCurrentUser} from 'app/hooks/reduxCreateSelectorHooks';
import {SidebarNotification} from 'app/components/sidebar/SidebarNotification';
import {UserStat} from 'app/components/sidebar/UserStat';
import {
  ExitToApp,
  InfoOutlined,
  PowerSettingsNewOutlined,
  SettingsOutlined,
  StarBorder,
  DeveloperMode,
  Code,
  ScienceOutlined,
  VerifiedUserOutlined,
  ContactSupportOutlined,
  BadgeOutlined,
} from '@mui/icons-material';
import {useExperimental} from 'app/hooks/useExperimental';
import {generateRandomPPID} from 'app/components/ads/usePPID';
import {useConsentCheckForPPID} from 'app/helpers/consent';
import {captureSentryException} from 'app/services/sentryLogging';
import {createSelector} from 'reselect';
import {useNative} from 'app/contexts/NativeContext';

const Container = styled.div`
  max-height: 100vh;
  ${props => props.theme.desktopLayout`
    max-height: calc(100vh - 4.5rem);
  `}
`;
const ListItems = styled.div`
  border-top: 1px solid ${COLORS.lightGrey};
  box-sizing: content-box;
  padding-top: 6px;
`;

const SCListItems = styled(ListItems)`
  border-top: none;
  padding-top: 0;
`;

const LinkContainer = styled(FlexContainer)`
  justify-content: center;
  padding: 2rem 0;
  a {
    text-decoration: none;
  }
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 1.5rem;
  padding-bottom: 0.825rem;
  border-bottom: 1px solid ${COLORS.lightGrey};
`;

const Name = styled.span`
  padding-top: 0.5rem;
  text-align: center;
  color: ${COLORS.black};
`;

const StatWrapper = styled.div`
  display: flex;
  padding-top: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const selectPathRegionSlug = createSelector(
  state => state.session,
  session => (session.district?.region ? `/${session.district.region.slug}` : '')
);

const UserMenu = ({closeDialog, animationDuration}) => {
  const regionPath = useSelector(selectPathRegionSlug);

  const location = useLocation();
  const dispatch = useDispatch();
  const consentChecked = useConsentCheckForPPID();
  const {user, claims, fuPaUser} = useSelector(selectCurrentUser);
  const experimental = useExperimental();
  const {isNativeApp, logout} = useNative();

  const handleLogout = async () => {
    try {
      const {logoutCurrentUser} = await import('app/services/firebase');
      await logoutCurrentUser();
      if (consentChecked) {
        generateRandomPPID(dispatch);
      }
      if (isNativeApp) {
        logout.handler();
      }
    } catch (e) {
      captureSentryException(e, {extra: {errorLocation: 'importing logoutCurrentUser on handleLogout'}});
    }
    closeDialog();
  };

  const handlePointsClick = () => {
    closeDialog();
    window.location.href = '/praemien';
  };

  const handleTickerClick = () => {
    closeDialog();
    // TODO: Add link to admin-backend
  };

  const devTools = fuPaUser?.permissions?.switchEnvironment ? (
    <LinkOnComponent enablehover={true.toString()} rel='nofollow' to={'/my/devtools'}>
      <SettingsRow icon={<DeveloperMode style={{color: COLORS.darkerGrey}} />} label='Entwicklertools' />
    </LinkOnComponent>
  ) : null;

  const adManagerConsole =
    fuPaUser?.userRole === 'admin' ? (
      <SettingsRow
        icon={<Code style={{color: COLORS.darkerGrey}} />}
        label='Google Publisher Console öffnen'
        onClick={() => window.googletag.openConsole()}
      />
    ) : null;

  const experimentalFeatures = experimental ? (
    <LinkOnComponent enablehover={true.toString()} rel='nofollow' to={'/my/experimental'}>
      <SettingsRow icon={<ScienceOutlined style={{color: COLORS.darkerGrey}} />} label='Experimentelle Funktionen' />
    </LinkOnComponent>
  ) : null;

  const logoutBtn = Object.keys(user).length ? (
    <ListItems>
      <SettingsRow
        onClick={handleLogout}
        icon={<PowerSettingsNewOutlined style={{color: COLORS.darkerGrey}} />}
        label='Abmelden'
      />
    </ListItems>
  ) : null;

  const protectedContent = Object.keys(user).length ? (
    <>
      <AccountInfo>
        <Avatar width={36} src={claims.picture} alt={`Profilbild von ${claims.name}`} />
        <Name>{user.displayName?.replace('+', ' ')}</Name>
        <Typography variant={'caption1'}>{user?.email}</Typography>
        <StatWrapper>
          <UserStat
            onStatsClick={handlePointsClick}
            animationDuration={animationDuration}
            count={fuPaUser?.rewardPoints}
            statName='Punkte'
          />
          <UserStat
            onStatsClick={handleTickerClick}
            animationDuration={animationDuration}
            count={fuPaUser?.tickerCount}
            statName='Liveticker'
          />
        </StatWrapper>
      </AccountInfo>
      <SidebarNotification closeSidebar={closeDialog} />
      <SCListItems onClick={closeDialog}>
        <LinkOnComponent enablehover={true.toString()} rel='nofollow' to={'/my/following'}>
          <SettingsRow icon={<StarBorder style={{color: COLORS.darkerGrey}} />} label='Favoriten' />
        </LinkOnComponent>
        <LinkOnComponent enablehover={true.toString()} rel='nofollow' to={'/my/settings'}>
          <SettingsRow icon={<SettingsOutlined style={{color: COLORS.darkerGrey}} />} label='Einstellungen' />
        </LinkOnComponent>
        {devTools}
        {adManagerConsole}
        {experimentalFeatures}
      </SCListItems>
    </>
  ) : (
    <LinkContainer>
      <Button
        onClick={() => (window.location = `/auth/login?redirectUrl=${encodeURIComponent(location.pathname)}`)}
        label='Anmelden'
      />
    </LinkContainer>
  );

  const VerifiedIcon = <VerifiedUserOutlined style={{color: COLORS.darkerGrey}} />;
  const SupportIcon = <ContactSupportOutlined style={{color: COLORS.darkerGrey}} />;
  const InfoIcon = <InfoOutlined style={{color: COLORS.darkerGrey}} />;
  const AboutIcon = <BadgeOutlined style={{color: COLORS.darkerGrey}} />;
  const AdminIcon = <ExitToApp style={{color: COLORS.darkerGrey}} />;
  return (
    <Container>
      <div>
        {protectedContent}
        <ListItems>
          <SettingsLinkRow icon={SupportIcon} label='Hilfebereich' link='https://support.fupa.net/support/home' />
          <LinkOnComponent enablehover={true.toString()} onClick={closeDialog} to={'/about'}>
            <SettingsRow icon={AboutIcon} label='Über FuPa' />
          </LinkOnComponent>
          <LinkOnComponent enablehover={true.toString()} onClick={closeDialog} to={'/about/privacy-policy'}>
            <SettingsRow icon={VerifiedIcon} label='Datenschutz' />
          </LinkOnComponent>
          <LinkOnComponent enablehover={true.toString()} onClick={closeDialog} to={`/about/legal-notice${regionPath}`}>
            <SettingsRow icon={InfoIcon} label='Impressum' />
          </LinkOnComponent>
          <SettingsLinkRow
            rel='nofollow'
            icon={AdminIcon}
            label='Vereinsverwaltung'
            link={`${process.env.ADMIN_URL}/fupa/admin/index.php`}
          />
        </ListItems>
      </div>
      {logoutBtn}
    </Container>
  );
};

export {UserMenu};
